import { useCallback, useState } from "react";
import { FiX } from "react-icons/fi";
import Modal from "react-responsive-modal";
import { useHistory } from "react-router";
import MaskedInput from "react-text-mask";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";

import * as Yup from 'yup';

import {Content,
        TitleContainer,
        ConsultantsAndDatesContainer,
        DatesContainer,
        ButtonsContainer} from "./styles";

import getValidationErrors from "../../utils/getValidationError";
import { isAfter } from "date-fns";

interface IModalRentReport{
    open: boolean;
    functionToCloseModal(): void;
}

export default function ModalRentReport({ open, functionToCloseModal }: IModalRentReport){
    const history = useHistory();

    const { dateToExpires, access_token } = useAuth();
    const { addToast } = useToast();

    const [initialDate, setInitialDate] = useState('');
    const [finalDate, setFinalDate] = useState('');

    const [loadingShowScreenRequest, setLoadingShowScreenRequest] = useState(false);
    const [loadingExcelRequest, setLoadingExcelRequest] = useState(false);

    const handleShowReport = useCallback(async() => {
        setLoadingShowScreenRequest(true);

        const data = {
            initial_date: initialDate,
            final_date: finalDate,
        }

        if(isAfter(new Date(`${initialDate.substring(6, 10)}-${initialDate.substring(3, 5)}-${initialDate.substring(0, 2)}`), new Date(`${finalDate.substring(6, 10)}-${finalDate.substring(3, 5)}-${finalDate.substring(0, 2)}`))){
            addToast({
                title: 'Coloque datas válidas!',
                type: 'error'
            });

            setLoadingShowScreenRequest(false);

            return;
        }

        try{
            const dateRegex = /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/;

            const schema = Yup.object().shape({
                initial_date: Yup.string().required('Digite uma data inicial!').matches(dateRegex, 'Coloque uma data válida!'),
                final_date: Yup.string().required('Digite uma data final!').matches(dateRegex, 'Coloque uma data válida!')
            });

            await schema.validate(data, {
                abortEarly: false
            });

            const token = await refreshToken(dateToExpires, access_token);

            api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token}`;

            const response = await api.post('/api/adm/reports/rents', {
                initial_date: `${initialDate.substring(6, 10)}-${initialDate.substring(3, 5)}-${initialDate.substring(0, 2)}`,
                final_date: `${finalDate.substring(6, 10)}-${finalDate.substring(3, 5)}-${finalDate.substring(0, 2)}`,
                type: 'screen'
            });

            if(!!response.data.success){
                if(response.data.data.length >= 1){
                    history.push({
                        pathname: '/show-rents-report',
                        state: { 
                            data: response.data.data, 
                            total: `${response.data.data[response.data.data.length - 1].total}`,
                            total_of_properties: `${response.data.data[response.data.data.length - 1].total_of_properties}`,
                            average_rent: `${response.data.data[response.data.data.length - 1].average_rent}`
                        }
                    });
    
                    functionToCloseModal();
                } else{
                    addToast({
                        type: 'info',
                        title: 'Relatório vazio'
                    });
                }
            }
        } catch (err:any){
            if(err instanceof Yup.ValidationError){
                const error = getValidationErrors(err);

                addToast({
                    title: error[Object.keys(error)[0]],
                    type: 'info',
                    description: ''
                });
            }

            if(!!err.response?.data.errors){
                const errorsNumber = Object.keys(err.response.data.errors).length;

                for(let i = 0; i < errorsNumber; i++){
                    err.response.data.errors[Object.keys(err.response.data.errors)[i]].forEach((error: string) => {
                        addToast({
                            title: error,
                            type: 'error',
                        }); 
                    })
                }
            };

           if(!!err.response?.data.message){
                addToast({
                    title: err.response.data.message,
                    type: 'error'
                });
            }

            console.log(err);
        }

        setLoadingShowScreenRequest(false);
    }, [dateToExpires, access_token, initialDate, finalDate, history, functionToCloseModal, addToast]);

    const handleExcelDownloadReport = useCallback(async() => {
        setLoadingExcelRequest(true);

        const data = {
            initial_date: initialDate,
            final_date: finalDate,
        }

        if(isAfter(new Date(`${initialDate.substring(6, 10)}-${initialDate.substring(3, 5)}-${initialDate.substring(0, 2)}`), new Date(`${finalDate.substring(6, 10)}-${finalDate.substring(3, 5)}-${finalDate.substring(0, 2)}`))){
            addToast({
                title: 'Coloque datas válidas!',
                type: 'error'
            });

            setLoadingExcelRequest(false);

            return;
        }

        try{
            const dateRegex = /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/;

            const schema = Yup.object().shape({
                initial_date: Yup.string().required('Digite uma data inicial!').matches(dateRegex, 'Coloque uma data válida!'),
                final_date: Yup.string().required('Digite uma data final!').matches(dateRegex, 'Coloque uma data válida!')
            });

            await schema.validate(data, {
                abortEarly: false
            });

            const token = await refreshToken(dateToExpires, access_token);

            api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token}`;

            const response = await api.post('/api/adm/reports/rents', {
                initial_date: `${initialDate.substring(6, 10)}-${initialDate.substring(3, 5)}-${initialDate.substring(0, 2)}`,
                final_date: `${finalDate.substring(6, 10)}-${finalDate.substring(3, 5)}-${finalDate.substring(0, 2)}`,
                type: 'excel'
            }, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Fechamentos.xlsx');
            link.click();
        } catch (err:any){
            if(err instanceof Yup.ValidationError){
                const error = getValidationErrors(err); 

                addToast({
                    title: error[Object.keys(error)[0]],
                    type: 'info',
                    description: ''
                });
            }

            else if(!!err.response.data.errors[Object.keys(err.response.data.errors)[0]][0]){
                const errorsNumber = Object.keys(err.response.data.errors).length;

                for(let i = 0; i < errorsNumber; i++){
                    err.response.data.errors[Object.keys(err.response.data.errors)[i]].forEach((error: string) => {
                        addToast({
                            title: error,
                            type: 'error',
                        }); 
                    })
                }
            };

           if(!!err.response?.data.message){
                addToast({
                    title: err.response.data.message,
                    type: 'error'
                });
            }

            console.log(err);
        }

        setLoadingExcelRequest(false);
    }, [dateToExpires, access_token, initialDate, finalDate, addToast]);

    return(
        <Modal open={open} onClose={functionToCloseModal} classNames={{
            modal: 'modalVisitsPerConsultant'
        }} closeIcon={(<FiX size={18} color="#000"/>)}>
            <Content>
                <TitleContainer>
                    <h3>FECHAMENTOS</h3>
                </TitleContainer>

                <ConsultantsAndDatesContainer>
                    <DatesContainer>
                        <p>Data inicial:</p>
                        <MaskedInput 
                            mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                            value={initialDate}
                            onChange={e => setInitialDate(e.target.value)}
                        />

                        <p>Data final:</p>
                        <MaskedInput 
                            mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                            value={finalDate}
                            onChange={e => setFinalDate(e.target.value)}
                        />
                    </DatesContainer>
                </ConsultantsAndDatesContainer>

                <ButtonsContainer>
                    <button onClick={!!loadingExcelRequest ? () => {} : handleExcelDownloadReport}>{!!loadingExcelRequest ? 'Carregando...' : "Excel"}</button>
                    <button onClick={!!loadingShowScreenRequest ? () => {} : handleShowReport}>{!!loadingShowScreenRequest ? 'Carregando...' : "Visualizar"}</button>
                </ButtonsContainer>
            </Content>
        </Modal>
    );
}